/**
 * O editor de plantas em tela cheia, na sua própria aba.
 *
 * Desenhar dentro de um card do dashboard era desenhar por um buraco: a
 * planta dividia a largura com o resto da página e a altura com tudo o que
 * vinha depois. Aqui fica só o menu do app em cima e o resto é papel.
 *
 * QUADRO 16:9, E ISSO É SOBRE ROLAGEM, não sobre estética. A tela de quem
 * desenha é 16:9, a TV onde a planta vai parar é 16:9, e o quadro em outra
 * proporção sobra numa direção e falta na outra — o que numa aba vira barra
 * de rolagem, e rolar enquanto se arrasta uma parede é como o traço sai
 * torto. Com a mesma proporção da tela, o desenho inteiro está sempre
 * visível e o gesto nunca disputa com a rolagem.
 *
 * Aba própria, e não modal: desenho é trabalho longo, e trabalho longo não
 * pode prender a aba onde a pessoa estava fazendo outra coisa.
 */

import { useParams } from "react-router-dom";

import { EditorDePlanta } from "@/components/EditorDePlanta";
import { PageHeader } from "@/components/PageHeader";
import { Loading } from "@/components/Loading";
import { EmptyState } from "@/components/EmptyState";
import { useAuth } from "@/contexts/AuthContext";
import { usePlantaDesenho, usePlantaImagem, useSites } from "@/hooks/queries";

export default function PlantaEditorPage() {
  const { siteId } = useParams<{ siteId: string }>();
  const auth = useAuth();
  const sites = useSites();
  const site = sites.data?.find((s) => s.id === siteId);

  const temArquivo = !!site?.planta_path;
  const imagem = usePlantaImagem(siteId, site?.planta_atualizada_em, temArquivo);
  const desenho = usePlantaDesenho(
    siteId, site?.planta_desenho_atualizado_em, !!site?.planta_desenho_atualizado_em);

  if (!siteId) return null;
  if (sites.isPending) return <Loading label="Abrindo a planta..." />;

  if (!auth.hasMinRole("admin")) {
    return (
      <EmptyState
        title="Sem permissão"
        description="Desenhar a planta de uma unidade é ação de administrador."
      />
    );
  }

  if (!site) {
    return (
      <EmptyState
        title="Unidade não encontrada"
        description="Ela pode ter sido removida, ou este link é de outro cliente."
      />
    );
  }

  // O editor lê `inicial` UMA vez — vira o primeiro passo do histórico — e
  // salvar grava o documento INTEIRO. Montado antes de o desenho chegar (aba
  // nova = cache vazio), abria em branco, e o primeiro salvar trocava o
  // desenho existente pelo vazio mais o traço novo. Com desenho gravado, só
  // monta depois de lê-lo; se a leitura falhar, não monta: editar às cegas
  // é apagar.
  const temDesenho = !!site.planta_desenho_atualizado_em;
  if (temDesenho && desenho.isPending) {
    return <Loading label="Abrindo o desenho..." />;
  }
  if (temDesenho && desenho.isError) {
    return (
      <EmptyState
        title="Não consegui abrir o desenho desta unidade"
        description="Recarregue a página. O editor só abre com o desenho salvo em mãos — abrir em branco e salvar apagaria o que já existe."
      />
    );
  }

  return (
    <div className="mx-auto max-w-[1600px]">
      <PageHeader
        title="Planta da unidade"
        description={site.name}
        voltar
        // Aba nova tem histórico vazio; sem destino declarado, o botão de
        // voltar não teria pra onde ir. Ver `PageHeader`.
        voltarPara="/"
        semAtualizar
      />

      <EditorDePlanta
        siteId={siteId}
        inicial={desenho.data ?? null}
        fundo={imagem.url}
        // Em tela cheia não há "fechar e voltar pro card": a saída é o botão
        // de voltar do cabeçalho, que já existe e já sabe pra onde ir.
        aoSair={() => window.close()}
      />
    </div>
  );
}
